/**
 * Pipeline session_sample field definitions for Diaper Search (filter + sort + table).
 * Keeps labels aligned with diaper-accuracy1 analysis output.
 */

import type { AnalysisSession } from './api-clip-monitor';

export type TriState = 'any' | 'yes' | 'no';

/** Boolean flags from the pipeline (tri-state filter each). */
export const BOOLEAN_SESSION_FIELDS: { key: keyof AnalysisSession | string; label: string }[] = [
  { key: 'flatline_like', label: 'Flatline-like' },
  { key: 'mostly_dry', label: 'Mostly dry' },
  { key: 'quickly_high_wetness', label: 'Quickly high wetness' },
  { key: 'left_truncated', label: 'Left truncated' },
  { key: 'right_truncated', label: 'Right truncated' },
  { key: 'session_complete', label: 'Session complete' },
  { key: 'baseline_trustworthy', label: 'Baseline trustworthy' },
  { key: 'backend_matched', label: 'Backend matched' },
  { key: 'is_day_start', label: 'Day start' },
];

/** Numeric metrics — min/max filter + sortable + columns. */
export const NUMERIC_SESSION_FIELDS: { key: string; label: string; decimals?: number }[] = [
  { key: 'model', label: 'Model code', decimals: 0 },
  { key: 'fw', label: 'Firmware', decimals: 0 },
  { key: 'counter', label: 'Diaper #', decimals: 0 },
  { key: 'session_order', label: 'Session order', decimals: 0 },
  { key: 'duration_hours', label: 'Duration (h)', decimals: 2 },
  { key: 'duration_sec', label: 'Duration (s)', decimals: 0 },
  { key: 'wetness_per_hour', label: 'Wetness/hr', decimals: 1 },
  { key: 'max_wetness', label: 'Max wetness', decimals: 0 },
  { key: 'baseline_wetness', label: 'Baseline wetness', decimals: 0 },
  { key: 'adc_rise_per_hour', label: 'ADC rise/hr', decimals: 2 },
  { key: 'wetness_to_adc_efficiency', label: 'Wetness/ADC eff.', decimals: 2 },
  { key: 'plateau_severity', label: 'Plateau severity', decimals: 2 },
  { key: 'dry_noise_std', label: 'Dry noise σ', decimals: 2 },
  { key: 'channel_imbalance_index', label: 'Channel imbalance', decimals: 3 },
  { key: 'early_to_late_growth_ratio', label: 'Early/late growth', decimals: 3 },
  { key: 'baseline_adc_sum', label: 'Baseline ADC sum', decimals: 0 },
  { key: 'max_adc_sum', label: 'Max ADC sum', decimals: 0 },
  { key: 'first_wet_delay_sec', label: 'First wet delay (s)', decimals: 0 },
  { key: 'time_to_max_wetness_sec', label: 'Time to max W (s)', decimals: 0 },
  { key: 'time_to_pooling_sec', label: 'Time to pool (s)', decimals: 0 },
  { key: 'n_records_raw', label: 'Records (raw)', decimals: 0 },
  { key: 'n_records_analysis', label: 'Records (analysis)', decimals: 0 },
  { key: 'expected_cadence_sec', label: 'Cadence (s)', decimals: 3 },
  { key: 'median_interval_sec', label: 'Median interval (s)', decimals: 3 },
  { key: 'p95_interval_sec', label: 'P95 interval (s)', decimals: 2 },
  { key: 'max_gap_sec', label: 'Max gap (s)', decimals: 2 },
  { key: 'gap_burden_sec', label: 'Gap burden (s)', decimals: 0 },
  { key: 'gap_burden_pct', label: 'Gap burden %', decimals: 2 },
  { key: 'attached_silent_sec', label: 'Attached silent (s)', decimals: 0 },
  { key: 'relay_switches', label: 'Relay switches', decimals: 0 },
  { key: 'relay_switches_per_hour', label: 'Relay sw/hr', decimals: 1 },
  { key: 'unique_relays', label: 'Unique relays', decimals: 0 },
  { key: 'missing_rssi_pct', label: 'Missing RSSI %', decimals: 1 },
  { key: 'battery_start', label: 'Battery start %', decimals: 0 },
  { key: 'battery_end', label: 'Battery end %', decimals: 0 },
  { key: 'battery_delta', label: 'Battery Δ', decimals: 0 },
  { key: 'battery_drop_per_day', label: 'Battery drop/day', decimals: 2 },
  { key: 'criterion_id', label: 'Criterion ID', decimals: 0 },
  { key: 'criterion_day_threshold', label: 'Criterion day %', decimals: 1 },
  { key: 'criterion_night_threshold', label: 'Criterion night %', decimals: 1 },
  { key: 'start_hour', label: 'Start hour', decimals: 0 },
];

/** Nested objects — shown as truncated JSON (not sortable). */
export const OBJECT_SESSION_FIELDS: { key: string; label: string }[] = [
  { key: 'phase_metrics', label: 'Phase metrics' },
  { key: 'gap_buckets', label: 'Gap buckets' },
];

/** String / text columns (search + display). */
export const STRING_SESSION_FIELDS: { key: string; label: string }[] = [
  { key: 'site_name', label: 'Site' },
  { key: 'start_date', label: 'Start date' },
  { key: 'criterion_name', label: 'Criterion' },
  { key: 'criterion_signature', label: 'Criterion sig.' },
  { key: 'clip', label: 'Clip stem' },
];

/** Default sort field (ISO string sorts lexicographically = chronological). */
export const DEFAULT_SORT_FIELD = 'start_ts';

/** All fields that can be sorted (primitives). */
export const SORTABLE_FIELDS: { key: string; label: string }[] = [
  { key: 'start_ts', label: 'Start time' },
  { key: 'end_ts', label: 'End time' },
  ...STRING_SESSION_FIELDS.map(({ key, label }) => ({ key, label })),
  ...BOOLEAN_SESSION_FIELDS.map(({ key, label }) => ({ key: String(key), label: `☑ ${label}` })),
  ...NUMERIC_SESSION_FIELDS,
];

export function getSessionField(row: AnalysisSession & Record<string, unknown>, key: string): unknown {
  if (key in row) return row[key];
  return undefined;
}

export function formatCell(value: unknown, decimals?: number): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Y' : '·';
  if (typeof value === 'number') {
    if (decimals !== undefined) return value.toFixed(decimals);
    if (Number.isInteger(value)) return String(value);
    return String(Math.round(value * 1000) / 1000);
  }
  if (typeof value === 'string') {
    return value.length > 32 ? `${value.slice(0, 29)}…` : value;
  }
  return String(value);
}

/** Truncated JSON for object columns (tooltip shows full value in UI). */
export function formatJsonPreview(value: unknown, maxLen = 44): string {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'object') return formatCell(value);
  try {
    const s = JSON.stringify(value);
    return s.length > maxLen ? `${s.slice(0, maxLen - 1)}…` : s;
  } catch {
    return '…';
  }
}

/** Tri-state boolean filter. */
export function matchesBooleanFilter(
  row: AnalysisSession & Record<string, unknown>,
  key: string,
  state: TriState
): boolean {
  if (state === 'any') return true;
  const v = row[key];
  if (state === 'yes') return v === true;
  return v === false;
}

/** Min/max for numeric fields (empty = no bound). */
export function matchesNumericFilter(
  row: AnalysisSession & Record<string, unknown>,
  key: string,
  minStr: string,
  maxStr: string
): boolean {
  const minTrim = minStr.trim();
  const maxTrim = maxStr.trim();
  if (!minTrim && !maxTrim) return true;
  const v = row[key];
  if (v === null || v === undefined) return false;
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return false;
  if (minTrim) {
    const min = parseFloat(minTrim);
    if (!Number.isNaN(min) && n < min) return false;
  }
  if (maxTrim) {
    const max = parseFloat(maxTrim);
    if (!Number.isNaN(max) && n > max) return false;
  }
  return true;
}

export function compareSessionField(
  a: AnalysisSession & Record<string, unknown>,
  b: AnalysisSession & Record<string, unknown>,
  key: string
): number {
  const va = getSessionField(a, key);
  const vb = getSessionField(b, key);
  if (va === vb) return 0;
  if (va === null || va === undefined) return -1;
  if (vb === null || vb === undefined) return 1;
  if (typeof va === 'boolean' && typeof vb === 'boolean') return (va === vb ? 0 : va ? 1 : -1);
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb);
  return String(va).localeCompare(String(vb));
}
